import React, { useState } from "react"
import { Button, Spin } from "antd"
import { useRequest } from "ahooks"
import ReviewList from "./components/ReviewList"
import SignedReviewForm from "./components/SignedReviewForm"
import UnsignedReviewForm from "./components/UnsignedReviewForm"
import ReviewPreview from "./components/ReviewPreview"
import { getReviewDetail } from "./detail"
import { getProjectGrossProfitReviewsApi } from "@/services/apis/project"
import type { ReviewListItem } from "./types"

/**
 * 项目毛利复盘
 *
 * 列表 → 表单 / 只读预览：
 *  - 未提交（none / draft）：已签约项目毛利复盘 / 未签约项目人力投入复盘表单（可编辑）
 *  - 已提交（submitted）：只读预览页（不可编辑，spec 4.9）
 *
 * 待复盘列表数据来自 GET /workbenchApi/projects/project-gross-profit-reviews：
 * 仅返回当前登录用户（管理所长）的全部复盘记录（含已提交），后端已按创建时间倒序；
 * 列表仅在挂载时加载一次，进入表单 / 返回列表不会重复请求，失败时可重试。
 * 提交成功后通过 onSubmitted 就地更新列表项状态（无需重新请求列表）。
 *
 * 详情数据来自 GET .../project-gross-profit-reviews/:uuid（复盘记录）
 * 与 GET .../:uuid/kpi-saves（BI 毛利 KPI 快照）、GET .../:uuid/submission（当前保存内容），
 * 装配逻辑见 ./detail.ts。
 */
const ProjectReview: React.FC = () => {
    const [selectedId, setSelectedId] = useState<string | null>(null)
    const [selectedItem, setSelectedItem] = useState<ReviewListItem | null>(null)

    const { data: list, loading, error, run, mutate } = useRequest(getProjectGrossProfitReviewsApi)
    const {
        data: detail,
        loading: detailLoading,
        error: detailError,
        run: loadDetail,
    } = useRequest(getReviewDetail, { manual: true })

    const openDetail = (id: string) => {
        setSelectedId(id)
        setSelectedItem((list ?? []).find((v) => v.id === id) ?? null)
        void loadDetail(id)
    }

    const backToList = () => {
        setSelectedId(null)
        setSelectedItem(null)
    }

    // 提交成功后就地更新列表项状态：该行立即显示「查看」，「待复盘」统计相应减 1（spec 2 / 5.3）
    const handleSubmitted = (id: string) => {
        mutate((prev) =>
            (prev ?? []).map((v) =>
                v.id === id ? { ...v, submissionStatus: "submitted" as const, submittedAt: new Date().toISOString() } : v
            )
        )
        setSelectedItem((prev) =>
            prev && prev.id === id ? { ...prev, submissionStatus: "submitted" as const } : prev
        )
    }

    const renderBody = () => {
        if (selectedId) {
            if (detailLoading) {
                return (
                    <div className="flex items-center justify-center py-24">
                        <Spin />
                    </div>
                )
            }
            if (detailError) {
                return (
                    <div className="flex flex-col items-center justify-center py-24 gap-3">
                        <div className="text-sm text-gray-500">复盘详情加载失败</div>
                        <Button type="primary" onClick={() => void loadDetail(selectedId)}>
                            重试
                        </Button>
                        <Button type="link" onClick={backToList}>
                            返回列表
                        </Button>
                    </div>
                )
            }
            if (!detail) return null

            // 打开模式：详情为准、列表状态兜底（spec 2）
            // Treat either source as a submitted signal.  The list and detail requests are
            // independent; if one is stale, never fall back to an editable form for a
            // record that the server says is locked.
            const isSubmitted =
                detail.savedSubmission?.status === "submitted" ||
                selectedItem?.submissionStatus === "submitted"

            if (isSubmitted) {
                return (
                    <ReviewPreview
                        detail={detail}
                        onBack={backToList}
                        onRetry={() => void loadDetail(selectedId)}
                    />
                )
            }

            return detail.kind === "signed" ? (
                <SignedReviewForm detail={detail} onBack={backToList} onSubmitted={handleSubmitted} />
            ) : (
                <UnsignedReviewForm detail={detail} onBack={backToList} onSubmitted={handleSubmitted} />
            )
        }

        if (loading) {
            return (
                <div className="flex items-center justify-center py-24">
                    <Spin />
                </div>
            )
        }
        if (error) {
            return (
                <div className="flex flex-col items-center justify-center py-24 gap-3">
                    <div className="text-sm text-gray-500">复盘列表加载失败</div>
                    <Button type="primary" onClick={() => void run()}>
                        重试
                    </Button>
                </div>
            )
        }
        return <ReviewList list={list ?? []} onSelect={openDetail} />
    }

    return (
        <div className="w-full h-full overflow-y-auto bg-gray-50 scrollbar-thin">{renderBody()}</div>
    )
}

export default ProjectReview
